import { inject, Injectable, signal } from '@angular/core';
import { BlockService } from './block.service';

@Injectable({
  providedIn: 'root',
})
export class BlockPermissionsStore {

  // Injects
  private readonly blockService = inject(BlockService);

  // Signals
  public readonly canWriteBlock = signal<boolean>(false);

  // Cache: block ID → can-edit result
  private readonly _canEditCache = new Map<number, boolean>();

  /** Fetches and stores block-level permissions for the current user. Call once after sign-in. */
  public load(): void {
    this.blockService.canWriteBlock().subscribe({
      next: (canWrite) => this.canWriteBlock.set(canWrite),
      error: () => this.canWriteBlock.set(false)
    });
  }

  /**
   * Returns a signal indicating whether the current user can edit the given block.
   * The result is fetched once per block ID and cached for the lifetime of the session.
   */
  public canEditBlock(id: number): ReturnType<typeof signal<boolean>> {
    const cached = this._canEditCache.get(id);
    const result = signal<boolean>(cached ?? false);

    if (cached === undefined) {
      this.blockService.canWriteSpecificBlock(id).subscribe({
        next: (canWrite) => {
          this._canEditCache.set(id, canWrite);
          result.set(canWrite);
        },
        error: () => {
          this._canEditCache.set(id, false);
        }
      });
    }

    return result;
  }

}
